/**
 * The signed-in user's pins: colleagues' public keys and client identities
 * they've verified. Client keys are shared only with pinned colleagues, so
 * the Hub can't slip in a key of its own (see `keyVault`).
 *
 * Stored on the Hub encrypted and authenticated under a key derived from the
 * user's private key: the Hub can't read them, and can't add or change one
 * without it being noticed (an altered blob simply fails to open). Each save
 * has a version, and this browser keeps its own copy of what it verified
 * (the newest version seen, every colleague's key, every client identity, the
 * engine accounts), so the Hub can't hand back an older copy either: an older
 * copy is refused and the browser's own verifications stand in its place, and
 * until the person has seen the warning nothing new is taken on the Hub's
 * word. A browser whose storage was cleared, or a new one, has only the Hub's.
 */
import { hkdf } from '@noble/hashes/hkdf';
import { hmac } from '@noble/hashes/hmac';
import { sha256 } from '@noble/hashes/sha256';
import { ref } from 'vue';
import { useAccountStore } from '~/stores/account';
import { base64ToBytes, bytesToBase64, concatBytes } from '~/util/bytes';
import * as hubClient from '~/util/hubClient';
import { sessionStill } from '~/util/sessionEpoch';
const VERSION = 1;
const INFO = new TextEncoder().encode('zunox/account-pins/v1');
const NONCE_BYTES = 12;
const empty = () => ({ users: {}, orgIdentities: {}, engines: {}, signing: {} });
async function pinsKey(privateKey, usage) {
    const raw = hkdf(sha256, privateKey, undefined, INFO, 32);
    return crypto.subtle.importKey('raw', raw, 'AES-GCM', false, [usage]);
}
export async function encryptPins(privateKey, pins) {
    const nonce = crypto.getRandomValues(new Uint8Array(NONCE_BYTES));
    const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce, additionalData: INFO }, await pinsKey(privateKey, 'encrypt'), new TextEncoder().encode(JSON.stringify(pins)));
    return bytesToBase64(concatBytes(Uint8Array.of(VERSION), nonce, new Uint8Array(ciphertext)));
}
/** Throws if the blob wasn't made with this private key, or was altered. */
export async function decryptPins(privateKey, blob) {
    const bytes = base64ToBytes(blob);
    if (bytes[0] !== VERSION)
        throw new Error('Unknown pins format.');
    const plaintext = await crypto.subtle.decrypt({
        name: 'AES-GCM',
        iv: bytes.subarray(1, 1 + NONCE_BYTES),
        additionalData: INFO,
    }, await pinsKey(privateKey, 'decrypt'), bytes.subarray(1 + NONCE_BYTES));
    const parsed = JSON.parse(new TextDecoder().decode(plaintext));
    return {
        users: parsed.users ?? {},
        orgIdentities: parsed.orgIdentities ?? {},
        engines: parsed.engines ?? {},
        signing: parsed.signing ?? {},
        activations: numbers(parsed.activations),
        version: typeof parsed.version === 'number' ? parsed.version : 0,
    };
}
function numbers(value) {
    const out = {};
    if (value && typeof value === 'object') {
        for (const [k, v] of Object.entries(value)) {
            const a = v;
            if (a && typeof a.seq === 'number' && Number.isFinite(a.seq) && typeof a.key === 'string')
                out[k] = { seq: a.seq, key: a.key };
        }
    }
    return out;
}
/** Each organization's newest activation in `a` and `b`; at the same `seq`
 * the one in `a` (seen first) stands. */
function newest(a, b) {
    const out = { ...(a ?? {}) };
    for (const [k, v] of Object.entries(b ?? {}))
        if (!out[k] || v.seq > out[k].seq)
            out[k] = v;
    return out;
}
let cache = null;
/** Goes up whenever the pins held change, so a getter that reads them
 * (`pinnedOrgIdentity`) is worked out again. */
const changes = ref(0);
function setCache(next) {
    cache = next;
    changes.value += 1;
}
/** Set when the Hub's copy was older than one this browser had seen, and so
 * refused: the Hub may have been restored from a backup, or handed back an
 * old copy on purpose. Stays set, through later saves (which start from the
 * verifications this browser kept), until the person has been told. While
 * it's set, nothing is pinned "as first seen": a client identity or key the
 * Hub offers after a rollback could be the point of the rollback. */
let rolledBack = null;
export function pinsRolledBack() {
    return rolledBack;
}
/** The person has been told about the refused copy. */
export function clearRolledBack() {
    rolledBack = null;
}
/** Whether a rollback was detected and not yet acknowledged: while so, nothing
 * is pinned on the Hub's word alone. */
export function pinsFrozen() {
    return rolledBack !== null;
}
function localKey(privateKey) {
    const id = hkdf(sha256, privateKey, undefined, new TextEncoder().encode('zunox/account-pins/local'), 8);
    return `zunox:pins:${bytesToBase64(id)}`;
}
/** The floor is sealed with a code only this person's account key can
 * make, so it can't be edited by someone else at the same computer (it lives
 * in this browser's storage, which they can reach): an edited or unsealed
 * floor counts as none. It can still be deleted, which costs only the
 * rollback warning for the next copy the Hub serves. */
function floorCode(privateKey, body) {
    const key = hkdf(sha256, privateKey, undefined, new TextEncoder().encode('zunox/account-pins/floor-mac'), 32);
    return bytesToBase64(hmac(sha256, key, new TextEncoder().encode(body)));
}
/** A sealed mark that this account's floor has been sealed in this browser:
 * from then on a plain (unsealed) floor is never read. */
const LEGACY_VERSION_MAX = 1000000;
function sealedMarkKey(privateKey) {
    return `${localKey(privateKey)}:sealed`;
}
function floorSealedBefore(privateKey) {
    try {
        return localStorage.getItem(sealedMarkKey(privateKey)) === floorCode(privateKey, 'sealed');
    }
    catch {
        return false;
    }
}
function readFloor(privateKey) {
    try {
        const raw = localStorage.getItem(localKey(privateKey));
        const sealed = raw ? JSON.parse(raw) : null;
        if (sealed && typeof sealed.body === 'string' && typeof sealed.code === 'string') {
            if (floorCode(privateKey, sealed.body) === sealed.code) {
                const parsed = JSON.parse(sealed.body);
                if (parsed && typeof parsed.version === 'number' && Array.isArray(parsed.engines))
                    return parsed;
            }
        }
        // The floor as this browser kept it before it was sealed (the release
        // before 1 November), read once, at the upgrade, exactly as that release
        // trusted it: its version still catches an older copy from the Hub, the
        // accounts it marked as the engine stay the engine's, and the keys and
        // identities it verified stand in for a refused copy. The first write
        // after seals it and marks this account's floor as sealed, so a plain
        // floor found after that (one put there by someone at the computer) is
        // ignored. A version no account could have reached is ignored too.
        if (sealed &&
            sealed.body === undefined &&
            !floorSealedBefore(privateKey) &&
            typeof sealed.version === 'number' &&
            Number.isInteger(sealed.version) &&
            sealed.version >= 0 &&
            sealed.version <= LEGACY_VERSION_MAX &&
            Array.isArray(sealed.engines)) {
            const strings = (v) => Object.fromEntries(Object.entries(v && typeof v === 'object' ? v : {}).filter((e) => typeof e[1] === 'string'));
            return {
                version: sealed.version,
                engines: sealed.engines.filter((id) => typeof id === 'string'),
                users: strings(sealed.users),
                orgIdentities: strings(sealed.orgIdentities),
                signing: strings(sealed.signing),
            };
        }
    }
    catch {
        // No storage here (or it was cleared): only the Hub's copy counts.
    }
    return { version: 0, engines: [] };
}
function floorOf(pins) {
    return {
        version: pins.version ?? 0,
        engines: Object.keys(pins.engines),
        users: pins.users,
        orgIdentities: pins.orgIdentities,
        signing: pins.signing,
        activations: pins.activations,
    };
}
/** The floor for `pins`, keeping what only this browser holds: the
 * activations (never lowered) and the identities pinned here. */
function floorKept(privateKey, pins) {
    const before = readFloor(privateKey);
    return {
        ...floorOf(pins),
        activations: newest(numbers(before.activations), pins.activations),
        localIdentities: before.localIdentities,
    };
}
function writeFloor(privateKey, floor) {
    try {
        localStorage.setItem(sealedMarkKey(privateKey), floorCode(privateKey, 'sealed'));
        const body = JSON.stringify(floor);
        localStorage.setItem(localKey(privateKey), JSON.stringify({ body, code: floorCode(privateKey, body) }));
    }
    catch {
        // As above.
    }
}
/** Whether every key verified in `kept` is in `offered`, unchanged. */
function keeps(offered, kept) {
    return Object.entries(kept ?? {}).every(([id, key]) => offered[id] === key);
}
/** The Hub's copy checked against what this browser has seen: an older one
 * is refused and this browser's own verifications stand in its place (a
 * browser with none kept has nobody verified, so nothing is shared), and
 * engine accounts this browser remembered stay remembered either way. A copy
 * at the very version this browser last saved must be that save: one that
 * differs from it (a verification gone, or a key replaced under the same
 * version) is refused the same way. A newer copy is taken as it is, since a
 * save from another of the person's browsers can change anything, with one
 * exception: a client identity this browser verified itself is never dropped
 * or replaced by a newer copy. Nothing in the app changes a pinned identity,
 * so a copy that does is not a save this browser can account for. The
 * identity verified here stays, the person is told, and the rest of the copy
 * (new colleagues, new clients) is taken. */
function withFloor(privateKey, pins) {
    const floor = readFloor(privateKey);
    const engines = { ...pins.engines };
    for (const id of floor.engines)
        engines[id] = true;
    const version = pins.version ?? 0;
    const older = version < floor.version;
    const conflicting = version === floor.version &&
        floor.version > 0 &&
        !(keeps(pins.users, floor.users) &&
            keeps(pins.orgIdentities, floor.orgIdentities) &&
            keeps(pins.signing, floor.signing));
    const lostIdentity = !older && !conflicting && !keeps(pins.orgIdentities, floor.orgIdentities);
    if (older)
        rolledBack = { hub: version, seen: floor.version };
    if (conflicting)
        rolledBack = { hub: version, seen: floor.version, conflict: true };
    if (lostIdentity)
        rolledBack = { hub: version, seen: floor.version, identity: true };
    // Activations only ever go up, whichever copy is taken; at one `seq`, the
    // one this browser kept stands.
    const activations = newest(numbers(floor.activations), pins.activations);
    const checked = older || conflicting
        ? {
            users: { ...(floor.users ?? {}) },
            orgIdentities: { ...(floor.orgIdentities ?? {}) },
            engines,
            signing: { ...(floor.signing ?? {}) },
            activations,
            version: floor.version,
        }
        : lostIdentity
            ? {
                ...pins,
                engines,
                activations,
                orgIdentities: { ...pins.orgIdentities, ...floor.orgIdentities },
            }
            : { ...pins, engines, activations };
    // The floor records only what was saved (so a save that never reached
    // the Hub is never taken for a rollback next time)...
    writeFloor(privateKey, floorKept(privateKey, checked));
    // ...and the pins used here add the identities pinned in this browser
    // whose save may not have gone through. A copy giving the client another
    // identity is refused for it.
    let orgIdentities = checked.orgIdentities;
    for (const [org, identity] of Object.entries(floor.localIdentities ?? {})) {
        const held = orgIdentities[org];
        if (held && held !== identity && !rolledBack)
            rolledBack = { hub: version, seen: floor.version, identity: true };
        orgIdentities = { ...orgIdentities, [org]: identity };
    }
    return { ...checked, orgIdentities };
}
/** The pins, from the Hub once per vault unlock. Unreadable pins count as
 * none: with nobody pinned, nothing is shared, which is the safe failure. */
export async function getPins() {
    return (await readPins()).pins;
}
/** Saves run one after another: two at once would each read the same copy
 * and the second would save over the first's change. */
let saving = Promise.resolve();
/** How many saves have finished, so a read can tell one landed meanwhile,
 * and how many are asked for and not yet over. */
let savesDone = 0;
let savesPending = 0;
/** Goes up on sign-out, so a save still running for the person who left
 * never changes the count for the next. */
let queueGeneration = 0;
/** The pins and whether they came from the Hub. A fetch that fails (the
 * Hub unreachable, a proxy's 502) is not an empty copy: it never counts as a
 * rollback, is not cached (so the next call asks again), and gives the
 * verifications this browser keeps for the person, sealed; a save refuses to
 * go on from it, since it would replace the Hub's copy with that alone. */
async function readPins(fromSave = false) {
    if (cache)
        return { pins: cache, fromHub: true };
    // Whose pins these are, taken before anything is awaited: if the person
    // signs out, or someone else signs in, while they are fetched, they are
    // neither kept nor handed back (another person's session must never
    // trust the identities this person verified).
    const held = useAccountStore().privateKeyBase64;
    if (!held)
        return { pins: empty(), fromHub: false };
    const privateKey = base64ToBytes(held);
    const session = sessionStill();
    const stillTheirs = () => session() && useAccountStore().privateKeyBase64 === held;
    // A read while a save is under way waits for it, and one that a save
    // finished during is read again: a copy fetched before a save landed is
    // older than the floor that save wrote, which is not a rollback.
    // (Not for ever: a Hub that never answers a save can't stall a read.)
    if (!fromSave && savesPending > 0) {
        let timer;
        await Promise.race([
            saving.catch(() => undefined),
            new Promise(r => {
                timer = setTimeout(r, 3000);
            }),
        ]);
        clearTimeout(timer);
        if (!stillTheirs())
            return { pins: empty(), fromHub: false };
        if (cache)
            return { pins: cache, fromHub: true };
    }
    const savesBefore = savesDone;
    let blob;
    try {
        blob = await hubClient.getPins();
    }
    catch {
        if (!stillTheirs())
            return { pins: empty(), fromHub: false };
        const floor = readFloor(privateKey);
        return {
            pins: {
                users: { ...(floor.users ?? {}) },
                orgIdentities: { ...(floor.orgIdentities ?? {}), ...(floor.localIdentities ?? {}) },
                engines: Object.fromEntries(floor.engines.map(id => [id, true])),
                signing: { ...(floor.signing ?? {}) },
                activations: numbers(floor.activations),
                version: floor.version,
            },
            fromHub: false,
        };
    }
    if (!stillTheirs())
        return { pins: empty(), fromHub: false };
    let opened;
    try {
        opened = blob ? await decryptPins(privateKey, blob) : empty();
    }
    catch {
        // A copy that won't open (altered, or not this person's) is refused like
        // an older one: the browser's own verifications stand in its place.
        opened = { ...empty(), version: -1 };
    }
    // Checked before withFloor, which records a rollback and writes the
    // floor: neither may happen for a person who has since gone.
    if (!stillTheirs())
        return { pins: empty(), fromHub: false };
    if (!fromSave && savesDone !== savesBefore)
        return readPins();
    const pins = withFloor(privateKey, opened);
    setCache(pins);
    return { pins, fromHub: true };
}
function update(change) {
    // Whose save this is, taken when it is asked for, not when its turn in the
    // queue comes: a save asked for by one person never runs for another.
    const asker = { held: useAccountStore().privateKeyBase64, session: sessionStill() };
    // Strictly after the save before it: two read-modify-write saves at once
    // against the Hub's single copy would lose one. (A save the Hub never
    // answers holds up later saves, never reads or unlocks: what matters is
    // kept in this browser's sealed copy at once.)
    const gen = queueGeneration;
    savesPending += 1;
    const run = saving
        .then(() => save(change, asker), () => save(change, asker))
        .finally(() => {
        if (gen === queueGeneration)
            savesPending = Math.max(0, savesPending - 1);
    });
    saving = run;
    return run;
}
async function save(change, asker) {
    // Saved for the account that asked, or not at all: a sign-out or another
    // sign-in, before or while it runs, ends it before anything is written.
    const held = asker.held;
    if (!held)
        throw new Error('Unlock your account first.');
    const privateKey = base64ToBytes(held);
    const stillTheirs = () => asker.session() && useAccountStore().privateKeyBase64 === held;
    const gone = () => new Error('Signed out while saving: it may not have been saved. Sign in and check.');
    if (!stillTheirs())
        throw gone();
    setCache(null);
    const { pins: current, fromHub } = await readPins(true);
    if (!stillTheirs())
        throw gone();
    if (!fromHub) {
        throw new Error("Couldn't read your verifications from the Hub, so nothing was saved. Try again.");
    }
    const next = { ...change(current), version: (current.version ?? 0) + 1 };
    const sealed = await encryptPins(privateKey, next);
    if (!stillTheirs())
        throw gone();
    await hubClient.putPins(sealed);
    if (!stillTheirs())
        throw gone();
    writeFloor(privateKey, floorKept(privateKey, next));
    savesDone += 1;
    setCache(next);
    return next;
}
/** Records that `publicKey` is verified as this colleague's. */
export function pinUser(userId, publicKey) {
    return update(pins => ({ ...pins, users: { ...pins.users, [userId]: publicKey } }));
}
/** Records a client's signing identity as first seen. Refused while a
 * rollback is unacknowledged: "first seen" then proves nothing. */
export function pinOrgIdentity(orgId, identity) {
    if (rolledBack)
        return Promise.reject(new Error('Verifications rolled back: not pinning anything new.'));
    // Kept in this browser's sealed copy at once, whatever becomes of the
    // save: a Hub that refuses the save never gets the identity left unpinned.
    const held = useAccountStore().privateKeyBase64;
    if (held) {
        const privateKey = base64ToBytes(held);
        const floor = readFloor(privateKey);
        if (!floor.localIdentities?.[orgId]) {
            writeFloor(privateKey, {
                ...floor,
                localIdentities: { ...(floor.localIdentities ?? {}), [orgId]: identity },
            });
        }
        if (cache && !cache.orgIdentities[orgId])
            setCache({ ...cache, orgIdentities: { ...cache.orgIdentities, [orgId]: identity } });
    }
    return update(pins => ({ ...pins, orgIdentities: { ...pins.orgIdentities, [orgId]: identity } }));
}
/** Records a colleague's key as verified and, when the person verifying says
 * so, that the account is the engine's, in ONE save: no saved copy of the
 * pins can then show the engine's account verified as ordinary staff, so an
 * older copy handed to a new browser gets it nothing. Engine status is never
 * taken away here (see `unpinEngine`). */
export function pinPerson(userId, publicKey, isEngine, signingKey = null) {
    return update(pins => {
        const signing = { ...pins.signing };
        if (signingKey)
            signing[userId] = signingKey;
        else
            delete signing[userId];
        return {
            ...pins,
            users: { ...pins.users, [userId]: publicKey },
            engines: isEngine ? { ...pins.engines, [userId]: true } : pins.engines,
            signing,
        };
    });
}
/** Forgets that `userId` is an engine account (made staff again), and the
 * signing key verified for it. */
export function unpinEngine(userId) {
    return update(pins => {
        const engines = { ...pins.engines };
        delete engines[userId];
        const signing = { ...pins.signing };
        delete signing[userId];
        return { ...pins, engines, signing };
    });
}
/** The client identity this browser has pinned for `orgId`, from the pins
 * already loaded (or the floor kept for them), without waiting: what a
 * synchronous trust check uses in place of the identity the Hub reports. */
export function pinnedOrgIdentity(orgId) {
    void changes.value;
    if (cache)
        return cache.orgIdentities[orgId] ?? null;
    const held = useAccountStore().privateKeyBase64;
    if (!held)
        return null;
    const floor = readFloor(base64ToBytes(held));
    return floor.localIdentities?.[orgId] ?? floor.orgIdentities?.[orgId] ?? null;
}
/** Records that activation `seq` of `key` was accepted for `orgId` (never
 * lowering it): at once in this browser's sealed copy (and the pins held),
 * whatever becomes of the save, then with the verifications on the Hub for
 * the person's other browsers. */
export function pinActivation(orgId, seq, key) {
    const held = useAccountStore().privateKeyBase64;
    if (!held)
        return Promise.reject(new Error('Unlock your account first.'));
    const privateKey = base64ToBytes(held);
    const add = { [orgId]: { seq, key } };
    const floor = readFloor(privateKey);
    writeFloor(privateKey, { ...floor, activations: newest(numbers(floor.activations), add) });
    if (cache)
        setCache({ ...cache, activations: newest(cache.activations, add) });
    return update(pins => ({ ...pins, activations: newest(pins.activations, add) }));
}
/** The newest activation accepted for `orgId`, from the pins held or this
 * browser's sealed copy, without waiting. */
export function acceptedActivation(orgId) {
    if (cache?.activations?.[orgId])
        return cache.activations[orgId];
    const held = useAccountStore().privateKeyBase64;
    if (!held)
        return null;
    return numbers(readFloor(base64ToBytes(held)).activations)[orgId] ?? null;
}
/** Forgets the cached pins, as on signing out. */
export function forgetPins() {
    setCache(null);
    rolledBack = null;
    // A save still waiting on the Hub belongs to whoever has gone: the next
    // person's saves never queue behind it (it can't write for them anyway).
    saving = Promise.resolve();
    savesPending = 0;
    queueGeneration += 1;
}
